import { GeneralOutboundFilter } from '@/services/outbound/generalOutbound/types'

/**
 * 메뉴 세션·URL 쿼리에 싣는 조회 조건 모양 (결정 12 — MOMS 공통 UX).
 *
 * 다른 메뉴를 다녀와도 조회 조건과 결과가 남아야 한다. 탭 전환이 react-router 언마운트라
 * 로컬 state 는 전부 날아가므로, 조건을 URL 에 실어 두고 마운트 때 되돌린다.
 * 저장은 `MenuStorage.menuOnSession.setSearch` 가 맡고 복원은 URL 에서 한다.
 *
 * 목록 검색어와 페이지 번호까지 싣는 이유는, 조건만 남고 보던 페이지가 1로 돌아가면
 * 사람에게는 여전히 "새로고침됐다" 로 보이기 때문이다(결정 12 13장).
 */
export interface GeneralOutboundSearchQuery {
  sprNm?: string
  ordNo?: string[]
  ordOptNo?: string[]
  ordType?: string
  trsType?: string
  /** 목록 검색어 — 현재 페이지 안에서 거르는 클라이언트 필터다. */
  listQuery?: string
  /** 페이지 번호. 쿼리스트링이라 문자열로 싣고 복원할 때 숫자로 되돌린다. */
  page?: string
}

/** 화면이 복원해서 쓰는 값 한 벌. */
export interface GeneralOutboundRestored {
  filter: GeneralOutboundFilter
  listQuery: string
  page: number
}

/**
 * 복원 결과 + 실제로 URL 에서 되돌린 게 있었는지.
 *
 * 화주는 비어 오면 세션 화주로 채우므로, 복원된 `filter.SPR_NM` 만 봐서는 최초 진입과 구분되지 않는다.
 * 그 둘을 섞으면 처음 들어온 사람에게도 조회가 돌아 "복원할 조건이 없으면 조회하지 않는다"(결정 12 11장)
 * 를 어긴다. URL 에 조건이 실제로 있었는지를 따로 들고 다닌다.
 */
export interface GeneralOutboundRestoreResult extends GeneralOutboundRestored {
  hasQuery: boolean
}

/**
 * 다섯 필드를 모두 싣는다.
 *
 * 주문번호·주문일련번호는 한때 뺐다. 단독 `core.FilterCodeInput` 이 자기 안에서 폼을 만들어 밖에서
 * 값을 심을 수 없었기 때문이다. `FilterArea` 로 바꾸면서 폼 인스턴스를 주입하게 되어 되돌릴 수 있다
 * (결정 12 19장 — 타입만 보고 빼지 말고 실제로 심어 보고 판단한다).
 *
 * 페이지 번호를 싣는 이유는 이 화면에 서버 페이지네이션이 있기 때문이다(한 페이지 5,000건).
 * 페이지 상태가 없는 화면이라면 싣지 않는다(결정 12 21장).
 */
export const toSearchQuery = (restored: GeneralOutboundRestored): GeneralOutboundSearchQuery => {
  const { filter, listQuery, page } = restored

  return {
    ...(filter.SPR_NM ? { sprNm: filter.SPR_NM } : {}),
    ...(filter.ORD_NO?.length ? { ordNo: filter.ORD_NO } : {}),
    ...(filter.ORD_OPT_NO?.length ? { ordOptNo: filter.ORD_OPT_NO } : {}),
    ...(filter.ORD_TYPE ? { ordType: filter.ORD_TYPE } : {}),
    ...(filter.TRS_TYPE ? { trsType: filter.TRS_TYPE } : {}),
    ...(listQuery ? { listQuery } : {}),
    // 1페이지는 기본값이라 싣지 않는다 — 쿼리스트링을 짧게 유지한다.
    ...(page > 1 ? { page: String(page) } : {}),
  }
}

/** 조작된 URL 에 배열·객체가 들어와도 폼이 깨지지 않게 문자열만 받는다. */
const text = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined)

/**
 * 다건 값은 배열로 싣지만 되돌아올 때 모양이 갈린다 — `qs.parse` 가 `comma: true` 라 한 건이면
 * 문자열로 온다. 둘 다 받아 배열로 맞춘다.
 */
const list = (value: unknown): string[] | undefined => {
  const raw = Array.isArray(value) ? value : [value]
  const items = raw.filter((v): v is string => typeof v === 'string' && Boolean(v))
  return items.length ? items : undefined
}

/** 1 이상의 정수만 페이지로 인정한다. `page=0`·`page=abc`·`page=-3` 은 1로 되돌린다. */
const restorePage = (value: unknown): number => {
  const parsed = Number(text(value))
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1
}

/**
 * 저장된 쿼리를 화면 값으로 되돌린다. 쿼리에 없는 항목은 기본값을 쓴다.
 * 화주는 기본값(로그인 세션 화주)이 있어 비어 오면 그걸 쓰고, 나머지는 빈 값으로 둔다.
 */
export const fromSearchQuery = (
  query: Partial<GeneralOutboundSearchQuery>,
  defaults: { SPR_NM: string },
): GeneralOutboundRestoreResult => ({
  filter: {
    SPR_NM: text(query.sprNm) ?? defaults.SPR_NM,
    ORD_NO: list(query.ordNo) ?? [],
    ORD_OPT_NO: list(query.ordOptNo) ?? [],
    ORD_TYPE: text(query.ordType),
    TRS_TYPE: text(query.trsType),
  },
  listQuery: text(query.listQuery) ?? '',
  page: restorePage(query.page),
  // 화주 하나만 있어도 조회를 되살린다 — 조회는 화주만 필수다.
  hasQuery: Boolean(text(query.sprNm)),
})
